export type SessionStore = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

export type MaxCartSelection = 'monthly' | 'yearly' | false | null;
export type AddonSelection = 'Year1' | 'Year2' | false | null;

export type AirBuySelection = {
  color: string;
  colorChosen: boolean;
  size: number | null;
  engravingEnabled: boolean | null;
  engravingMessage: string | null;
  maxCart: MaxCartSelection;
  tradeInDevice: string | null;
  tradeInSerial: string;
  tradeInDeclined: boolean;
  clueCart: AddonSelection;
};

export type ProBuySelection = {
  color: string;
  colorChosen: boolean;
  size: number | null;
  chargerCaseColor: string | null;
  engravingEnabled: boolean;
  engravingMessage: string | null;
  maxCart: MaxCartSelection;
  tradeInDevice: string | null;
  tradeInSerial: string;
  tradeInDeclined: boolean;
};

export const AIR_SELECTION_KEY = 'uh-ring-air-selection';
export const PRO_SELECTION_KEY = 'uh-ring-pro-selection';

const MAX_CART = new Set(['monthly', 'yearly', false, null]);
const ADDON = new Set(['Year1', 'Year2', false, null]);

function browserSession(): SessionStore | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

function readJson(key: string, storage?: SessionStore): unknown {
  const store = storage ?? browserSession();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown, storage?: SessionStore): void {
  const store = storage ?? browserSession();
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode and quota errors leave the in-memory funnel as-is.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function maxCartOf(value: unknown): MaxCartSelection | undefined {
  if (value === false || value === null) return value;
  if (value === 'monthly' || value === 'yearly') return value;
  return undefined;
}

function addonOf(value: unknown): AddonSelection | undefined {
  if (value === false || value === null) return value;
  if (value === 'Year1' || value === 'Year2') return value;
  return undefined;
}

function sizeOf(value: unknown): number | null | undefined {
  if (value === null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return undefined;
}

function stringOrNull(value: unknown): string | null | undefined {
  if (value === null) return null;
  if (typeof value === 'string') return value;
  return undefined;
}

export function parseAirBuySelection(value: unknown): AirBuySelection | null {
  if (!isRecord(value)) return null;
  const maxCart = maxCartOf(value.maxCart);
  const clueCart = addonOf(value.clueCart);
  const size = sizeOf(value.size);
  const color = stringOrNull(value.color);
  const tradeInDevice = stringOrNull(value.tradeInDevice);
  const tradeInSerial = value.tradeInSerial;
  if (
    typeof value.color !== 'string' ||
    typeof value.colorChosen !== 'boolean' ||
    size === undefined ||
    (value.engravingEnabled !== null && typeof value.engravingEnabled !== 'boolean') ||
    (value.engravingMessage !== null && typeof value.engravingMessage !== 'string') ||
    maxCart === undefined ||
    tradeInDevice === undefined ||
    typeof tradeInSerial !== 'string' ||
    typeof value.tradeInDeclined !== 'boolean' ||
    clueCart === undefined ||
    color === undefined ||
    color === null
  ) {
    return null;
  }
  if (!MAX_CART.has(maxCart) || !ADDON.has(clueCart)) return null;
  return {
    color,
    colorChosen: value.colorChosen,
    size,
    engravingEnabled: value.engravingEnabled as boolean | null,
    engravingMessage: value.engravingMessage as string | null,
    maxCart,
    tradeInDevice,
    tradeInSerial,
    tradeInDeclined: value.tradeInDeclined,
    clueCart,
  };
}

export function parseProBuySelection(value: unknown): ProBuySelection | null {
  if (!isRecord(value)) return null;
  const maxCart = maxCartOf(value.maxCart);
  const size = sizeOf(value.size);
  const color = stringOrNull(value.color);
  const chargerCaseColor = stringOrNull(value.chargerCaseColor);
  const tradeInDevice = stringOrNull(value.tradeInDevice);
  if (
    color === undefined ||
    color === null ||
    typeof value.colorChosen !== 'boolean' ||
    size === undefined ||
    chargerCaseColor === undefined ||
    typeof value.engravingEnabled !== 'boolean' ||
    (value.engravingMessage !== null && typeof value.engravingMessage !== 'string') ||
    maxCart === undefined ||
    tradeInDevice === undefined ||
    typeof value.tradeInSerial !== 'string' ||
    typeof value.tradeInDeclined !== 'boolean'
  ) {
    return null;
  }
  return {
    color,
    colorChosen: value.colorChosen,
    size,
    chargerCaseColor,
    engravingEnabled: value.engravingEnabled,
    engravingMessage: value.engravingMessage as string | null,
    maxCart,
    tradeInDevice,
    tradeInSerial: value.tradeInSerial,
    tradeInDeclined: value.tradeInDeclined,
  };
}

function selectionKey(base: string, region: string): string {
  return `${base}:${region}`;
}

export function readAirBuySelection(
  region: string,
  storage?: SessionStore,
): AirBuySelection | null {
  if (!region) return null;
  return parseAirBuySelection(readJson(selectionKey(AIR_SELECTION_KEY, region), storage));
}

export function writeAirBuySelection(
  value: AirBuySelection,
  region: string,
  storage?: SessionStore,
): void {
  if (!region) return;
  writeJson(selectionKey(AIR_SELECTION_KEY, region), value, storage);
}

export function readProBuySelection(
  region: string,
  storage?: SessionStore,
): ProBuySelection | null {
  if (!region) return null;
  return parseProBuySelection(readJson(selectionKey(PRO_SELECTION_KEY, region), storage));
}

export function writeProBuySelection(
  value: ProBuySelection,
  region: string,
  storage?: SessionStore,
): void {
  if (!region) return;
  writeJson(selectionKey(PRO_SELECTION_KEY, region), value, storage);
}

/** A campaign colour and an affiliate Max decline replace the stored answers. */
export function withAirSelectionOverrides(
  saved: AirBuySelection | null,
  opts: { urlColor?: string | null; affiliate?: boolean },
): AirBuySelection | null {
  if (!saved) return null;
  return {
    ...saved,
    ...(opts.urlColor ? { color: opts.urlColor, colorChosen: true } : {}),
    ...(opts.affiliate ? { maxCart: false as const } : {}),
  };
}

export function withProSelectionOverrides(
  saved: ProBuySelection | null,
  opts: { urlColor?: string | null; affiliate?: boolean },
): ProBuySelection | null {
  if (!saved) return null;
  return {
    ...saved,
    ...(opts.urlColor ? { color: opts.urlColor, colorChosen: true } : {}),
    ...(opts.affiliate ? { maxCart: false as const } : {}),
  };
}
